import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { normalizarDocumento } from '@/lib/fiscal/sped/documentos';
import { useClientesPermitidos } from '@/hooks/usuarios/useUsuarioClientes';

export type ClienteToken = { cnpj: string; nome: string };

// fiscal_cliente ainda não está nos tipos gerados.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

/** Nomes do cadastro fiscal_cliente (Tokens API) por CNPJ/CPF. Sem a tabela (migration não aplicada), volta vazio. */
export async function carregarNomesClientes(): Promise<Map<string, string>> {
  const { data, error } = await sb.from('fiscal_cliente').select('cnpj_cpf, nome');
  if (error) return new Map();
  return new Map(((data || []) as { cnpj_cpf: string; nome: string }[]).map((c) => [c.cnpj_cpf, c.nome]));
}

/**
 * Clientes do cadastro mestre (Tributação › Tokens API): um por CNPJ/CPF, ignorando tokens revogados.
 * O nome exibido é o do cadastro fiscal_cliente; sem cadastro, o do token mais recente do CNPJ.
 * O usuário Restrito só recebe os clientes vinculados a ele.
 */
export function useClientesToken({ enabled = true }: { enabled?: boolean } = {}) {
  const permitidos = useClientesPermitidos();
  const filtrar = useCallback((lista: ClienteToken[]) => lista.filter((c) => permitidos.pode(c.cnpj)), [permitidos]);
  return useQuery({
    queryKey: ['tributacao', 'clientes-token'],
    staleTime: 5 * 60 * 1000,
    enabled,
    queryFn: async (): Promise<ClienteToken[]> => {
      const [{ data, error }, nomes] = await Promise.all([
        supabase
          .from('tributos_api_tokens')
          .select('nome, cliente_cnpj_cpf, created_at')
          .not('cliente_cnpj_cpf', 'is', null)
          .is('revoked_at', null)
          .order('created_at', { ascending: false }),
        carregarNomesClientes(),
      ]);
      if (error) throw error;
      const porCnpj = new Map<string, ClienteToken>();
      for (const t of data || []) {
        const cnpj = normalizarDocumento(t.cliente_cnpj_cpf as string | null);
        if (cnpj && !porCnpj.has(cnpj)) porCnpj.set(cnpj, { cnpj, nome: nomes.get(cnpj) || (t.nome as string) || cnpj });
      }
      return [...porCnpj.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },
    select: filtrar,
  });
}
